// All site content data — edit services, industries, FAQs and insights here.

export const CATEGORIES = [
  { id: "iso", label: "Management Systems & ISO Standards" },
  { id: "cyber", label: "Cybersecurity & Technical Assurance" },
  { id: "payment", label: "Payment Security & Compliance" },
  { id: "tech", label: "Technology Consulting & Development" },
];

export const SERVICES = [
  {
    id: "iso-27001",
    category: "iso",
    icon: "shield",
    name: "ISO 27001 Information Security Management",
    shortName: "ISO 27001 Consulting",
    summary:
      "End-to-end consulting for designing, implementing and improving an information security management system aligned with ISO 27001.",
    covers: [
      "ISMS scoping and gap assessment",
      "Information security risk assessment",
      "Statement of Applicability development",
      "Policy and control documentation",
      "Control implementation support",
      "Awareness and internal-audit readiness",
      "Certification readiness and continual improvement",
    ],
    audience:
      "Organizations that handle sensitive or regulated information — including banks, fintechs, technology providers and professional-services firms — preparing for certification or strengthening an existing ISMS.",
    challenges: [
      "Unclear ISMS scope and asset boundaries",
      "Fragmented policies and undocumented controls",
      "Uncertain risk ownership and treatment decisions",
      "Pressure from clients, partners or regulators to demonstrate security",
    ],
    activities: [
      "Scoping workshops and current-state review",
      "Facilitated risk assessment and treatment planning",
      "Statement of Applicability development",
      "Policy, procedure and record framework drafting",
      "Control implementation and awareness support",
      "Internal-audit preparation and management-review guidance",
    ],
    benefits: [
      "A structured, auditable information security management system",
      "Clearer visibility of information-security risk and ownership",
      "Improved readiness for independent certification assessment",
      "Stronger confidence from customers, partners and regulators",
    ],
    note: "Numeric Consulting & Solutions provides consulting and certification-readiness support. Certification decisions are made by independent, accredited certification bodies.",
  },
  {
    id: "iso-22301",
    category: "iso",
    icon: "continuity",
    name: "ISO 22301 Business Continuity Management",
    shortName: "ISO 22301 Consulting",
    summary:
      "Business continuity management system consulting covering analysis, strategy, plans, exercising and certification readiness.",
    covers: [
      "Business impact analysis and risk assessment",
      "Continuity strategy development",
      "Business continuity plans and crisis-management procedures",
      "Exercising, testing and documentation",
      "Certification readiness support",
    ],
    audience:
      "Organizations whose operations must withstand disruption — financial institutions, payment providers, technology platforms and essential service organizations.",
    challenges: [
      "Continuity plans that exist on paper but have never been tested",
      "Unknown recovery priorities and dependencies",
      "Regulatory expectations for operational resilience",
      "Unclear crisis roles, escalation and communication paths",
    ],
    activities: [
      "Business impact analysis workshops",
      "Continuity risk assessment",
      "Strategy selection and plan development",
      "Crisis-management and communication procedures",
      "Exercise design, facilitation and post-exercise reporting",
    ],
    benefits: [
      "Continuity arrangements grounded in real business priorities",
      "Tested plans and clearer crisis decision-making",
      "Demonstrable operational-resilience capability",
      "Readiness for ISO 22301 certification assessment",
    ],
    note: "We support certification readiness; certification itself is awarded by independent certification bodies.",
  },
  {
    id: "iso-9001",
    category: "iso",
    icon: "quality",
    name: "ISO 9001 Quality Management",
    shortName: "ISO 9001 Consulting",
    summary:
      "Quality management system consulting that connects process discipline with measurable performance and continual improvement.",
    covers: [
      "Process mapping and quality objectives",
      "QMS documentation and records",
      "Performance monitoring and measurement",
      "Internal-audit readiness and corrective action",
      "Continual improvement mechanisms",
    ],
    audience:
      "Organizations seeking consistent service delivery, stronger process control and a quality management system aligned with ISO 9001.",
    challenges: [
      "Inconsistent processes across teams or locations",
      "Documentation that does not reflect how work is actually done",
      "Weak corrective-action follow-through",
      "Limited performance measurement",
    ],
    activities: [
      "Process mapping and interaction analysis",
      "Quality objectives and performance indicators",
      "Documentation development and simplification",
      "Internal-audit preparation",
      "Corrective-action and improvement coaching",
    ],
    benefits: [
      "More consistent, controlled service delivery",
      "Clear accountability for process performance",
      "A practical QMS that supports certification readiness",
      "A working cycle of measurement and improvement",
    ],
    note: "Certification readiness support only; certification is issued by accredited certification bodies.",
  },
  {
    id: "iso-38500",
    category: "iso",
    icon: "governance",
    name: "ISO 38500 IT Governance",
    shortName: "ISO 38500 IT Governance",
    summary:
      "Guidance for aligning IT decision-making, accountability and performance with organizational objectives, informed by ISO/IEC 38500 principles.",
    covers: [
      "IT governance framework review",
      "Decision rights and accountability structures",
      "Strategy, performance and conformance alignment",
      "Responsible and effective use of technology",
    ],
    audience:
      "Boards, executives and technology leaders who need clearer accountability for technology investment, risk and performance.",
    challenges: [
      "Technology decisions disconnected from business strategy",
      "Unclear accountability between business and IT",
      "Limited visibility of technology risk at leadership level",
      "Investment decisions without consistent evaluation criteria",
    ],
    activities: [
      "Governance current-state assessment",
      "Principle and accountability framework design",
      "Decision-making and escalation model definition",
      "Performance and conformance reporting guidance",
    ],
    benefits: [
      "Clearer ownership of technology decisions and risk",
      "Better alignment between technology and organizational objectives",
      "More disciplined evaluation of technology investments",
      "Stronger board- and executive-level visibility",
    ],
  },
  {
    id: "iso-training",
    category: "iso",
    icon: "training",
    name: "ISO Standards Training",
    shortName: "ISO Standards Training",
    summary:
      "Practical training on ISO management-system standards — from executive briefings to implementation workshops and internal-auditor development.",
    covers: [
      "Executive and board briefings",
      "Implementation workshops",
      "Employee awareness programmes",
      "Internal-auditor training",
      "Customized organizational training",
    ],
    audience:
      "Leadership teams, implementation teams, internal auditors and employees who need a working understanding of ISO management-system standards.",
    challenges: [
      "Limited internal expertise in management-system standards",
      "Awareness gaps that weaken control adoption",
      "Internal audits that lack structure or confidence",
      "Training that is generic rather than role-relevant",
    ],
    activities: [
      "Training-needs discussion and curriculum design",
      "Instructor-led workshops and briefings",
      "Practical exercises based on your environment",
      "Internal-auditor technique coaching",
    ],
    benefits: [
      "A workforce that understands its role in the management system",
      "More capable internal audit capacity",
      "Faster, better-adopted implementation",
      "Shared vocabulary between leadership and practitioners",
    ],
    note: "Courses are professional training programmes; they are not described as accredited or certified courses unless explicitly confirmed.",
  },
  {
    id: "cyber-assessments",
    category: "cyber",
    icon: "assessment",
    name: "Cybersecurity Assessments",
    shortName: "Cybersecurity Assessment",
    summary:
      "Structured reviews of your security posture, controls and practices, producing risk-prioritized, practical improvement roadmaps.",
    covers: [
      "Security posture and control reviews",
      "Gap identification against recognized practices",
      "Risk prioritization",
      "Improvement roadmap development",
    ],
    audience:
      "Organizations that need an honest, structured view of their cybersecurity posture before investing in improvements or facing external review.",
    challenges: [
      "Unknown weaknesses across people, process and technology",
      "Security spend not clearly tied to risk reduction",
      "Difficulty answering board, client or regulator security questions",
      "Controls that have grown without an overall design",
    ],
    activities: [
      "Control and documentation review",
      "Stakeholder interviews and technical observation",
      "Gap analysis and risk rating",
      "Prioritized, phased improvement roadmap",
    ],
    benefits: [
      "A clear, evidence-based picture of current security posture",
      "Investment focused on the most material risks",
      "A defensible basis for answering stakeholder questions",
      "A practical path from assessment to improvement",
    ],
  },
  {
    id: "vapt",
    category: "cyber",
    icon: "target",
    name: "Vulnerability Assessment & Penetration Testing",
    shortName: "VAPT",
    summary:
      "Vulnerability identification and controlled security testing that validates real-world risk and produces evidence-based, prioritized findings.",
    covers: [
      "Vulnerability identification across agreed scope",
      "Controlled penetration testing",
      "Risk validation and exploitation analysis",
      "Evidence-based findings and reporting",
      "Prioritized remediation recommendations",
    ],
    audience:
      "Organizations that need technical assurance over applications, infrastructure or networks — before an attacker, auditor or regulator finds the weaknesses first.",
    challenges: [
      "Unknown exploitable weaknesses in systems and applications",
      "Compliance or client requirements for independent testing",
      "Findings from previous tests that were never validated or closed",
      "Difficulty distinguishing theoretical from practical risk",
    ],
    activities: [
      "Scope definition and rules of engagement",
      "Automated and manual vulnerability identification",
      "Controlled exploitation to validate risk",
      "Findings documentation with evidence",
      "Remediation guidance and retesting",
    ],
    benefits: [
      "Validated view of exploitable weaknesses",
      "Findings prioritized by real business risk",
      "Evidence suitable for auditors, clients and regulators",
      "Clear remediation direction for technical teams",
    ],
  },
  {
    id: "vapt-remediation",
    category: "cyber",
    icon: "remediation",
    name: "VAPT Remediation Support",
    shortName: "VAPT Remediation",
    summary:
      "Structured support to understand findings, prioritize corrective actions, validate remediation and strengthen the controls behind the weaknesses.",
    covers: [
      "Findings interpretation and walkthroughs",
      "Remediation prioritization and planning",
      "Corrective-action tracking",
      "Remediation validation and retesting",
      "Control improvement to prevent recurrence",
    ],
    audience:
      "Organizations that have assessment or penetration-test findings and need practical support turning them into completed, verified fixes.",
    challenges: [
      "Findings that remain open long after a report is delivered",
      "Technical teams stretched between delivery and remediation",
      "Unclear prioritization across long findings lists",
      "Repeat findings in successive assessments",
    ],
    activities: [
      "Findings review sessions with technical teams",
      "Risk-based remediation sequencing",
      "Remediation tracking and status reporting",
      "Fix validation and retesting",
      "Root-cause and control-strengthening guidance",
    ],
    benefits: [
      "Faster, verified closure of security findings",
      "Remediation effort focused on highest-risk items first",
      "Reduced recurrence through control improvement",
      "Clear remediation evidence for stakeholders",
    ],
  },
  {
    id: "swift-csp",
    category: "cyber",
    icon: "swift",
    name: "SWIFT CSP Assessments",
    shortName: "SWIFT CSP Assessment",
    summary:
      "Assessments against applicable SWIFT Customer Security Controls Framework requirements, with evidence review, gap identification and remediation support.",
    covers: [
      "Assessment against applicable CSCF controls",
      "Evidence collection and review",
      "Gap identification and risk context",
      "Remediation planning and support",
    ],
    audience:
      "Banks and financial institutions connected to the SWIFT network that must attest against the Customer Security Programme.",
    challenges: [
      "Interpreting CSCF control requirements in a specific environment",
      "Assembling sufficient, well-organized evidence",
      "Addressing gaps ahead of attestation deadlines",
      "Sustaining compliance between assessment cycles",
    ],
    activities: [
      "Scope and architecture review",
      "Control-by-control assessment and evidence review",
      "Gap documentation and risk discussion",
      "Remediation guidance and readiness tracking",
    ],
    benefits: [
      "A clear picture of CSCF alignment and gaps",
      "Better-organized evidence for attestation",
      "Prioritized remediation before deadlines",
      "Stronger security hygiene across the SWIFT environment",
    ],
    note: "Numeric Consulting & Solutions is not presented as a SWIFT-accredited assessor; services are assessment and readiness support.",
  },
  {
    id: "pci-dss",
    category: "payment",
    icon: "payment",
    name: "PCI DSS Consulting & Certification Readiness",
    shortName: "PCI DSS Consulting",
    summary:
      "Payment card data security consulting — from scope determination and gap assessment through remediation support and readiness for formal assessment.",
    covers: [
      "Cardholder-data environment scope determination",
      "Gap assessment against PCI DSS requirements",
      "Control improvement and documentation",
      "Remediation support",
      "Readiness for formal assessment",
    ],
    audience:
      "Merchants, payment service providers, fintechs and banks that store, process or transmit cardholder data.",
    challenges: [
      "Unclear or unnecessarily wide PCI DSS scope",
      "Requirement interpretation in complex environments",
      "Documentation and evidence gaps",
      "Remediation pressure ahead of formal assessment",
    ],
    activities: [
      "Data-flow and scope analysis",
      "Requirement-by-requirement gap assessment",
      "Remediation planning and control improvement",
      "Policy, procedure and evidence development",
      "Readiness review before formal assessment",
    ],
    benefits: [
      "Right-sized, well-understood compliance scope",
      "A clear, prioritized path to readiness",
      "Reduced assessment surprises",
      "Stronger protection of cardholder data",
    ],
    note: "Numeric Consulting & Solutions provides consulting and readiness support; formal PCI DSS assessment and certification are performed by Qualified Security Assessors.",
  },
  {
    id: "it-consulting",
    category: "tech",
    icon: "consulting",
    name: "IT Consulting",
    shortName: "IT Consulting",
    summary:
      "Technology advisory, solution evaluation, implementation guidance and third-party technology support grounded in enterprise experience.",
    covers: [
      "Technology strategy and advisory",
      "Solution evaluation and selection support",
      "Implementation planning and guidance",
      "Third-party technology products and services support",
    ],
    audience:
      "Organizations making significant technology decisions or needing experienced guidance through implementation and vendor landscapes.",
    challenges: [
      "Technology choices made without independent evaluation",
      "Vendor complexity and unclear total cost",
      "Implementations that drift from business intent",
      "Limited internal capacity for specialist technology decisions",
    ],
    activities: [
      "Requirements and options analysis",
      "Vendor and solution evaluation",
      "Implementation roadmap and governance support",
      "Ongoing advisory through delivery",
    ],
    benefits: [
      "Better-informed, lower-risk technology decisions",
      "Independent perspective in vendor conversations",
      "Implementations aligned with business objectives",
      "Access to experienced technology counsel",
    ],
  },
  {
    id: "app-development",
    category: "tech",
    icon: "development",
    name: "Application Development",
    shortName: "Application Development",
    summary:
      "Secure, scalable and business-aligned application development, informed by our security and governance consulting practice.",
    covers: [
      "Requirements analysis and solution design",
      "Secure application development",
      "Integration with existing enterprise systems",
      "Testing, deployment and handover support",
    ],
    audience:
      "Organizations that need business applications built with security, maintainability and governance in mind from the start.",
    challenges: [
      "Applications built without security-by-design",
      "Solutions that fail to integrate with existing systems",
      "Unclear requirements leading to rework",
      "Limited post-delivery documentation and knowledge transfer",
    ],
    activities: [
      "Requirements workshops and specification",
      "Architecture and secure-design review",
      "Iterative development with regular demonstrations",
      "Testing, documentation and handover",
    ],
    benefits: [
      "Applications aligned to real business processes",
      "Security considered from design through delivery",
      "Documented, maintainable solutions",
      "Smooth adoption through structured handover",
    ],
  },
];

export const INDUSTRIES = [
  {
    id: "banking",
    icon: "bank",
    name: "Banking & Financial Services",
    description:
      "Banks operate under intense regulatory scrutiny, constant threat pressure and high expectations for resilience. We support information security, business continuity, payment security and governance programmes that stand up to examination.",
    concerns: ["Regulatory obligations", "Information security", "Operational resilience", "Payment security", "Third-party risk"],
  },
  {
    id: "fintech",
    icon: "fintech",
    name: "Fintech & Payment Services",
    description:
      "Fintechs and payment providers must earn trust quickly — from customers, banking partners, card schemes and regulators. We help build security and compliance programmes that support growth rather than slow it.",
    concerns: ["Customer trust", "PCI DSS readiness", "Partner due diligence", "Security-by-design", "Scalable compliance"],
  },
  {
    id: "technology",
    icon: "technology",
    name: "Technology Companies",
    description:
      "Technology companies face client security questionnaires, enterprise procurement requirements and the need to demonstrate mature practices. We help formalize security, quality and governance without losing agility.",
    concerns: ["Client assurance", "Enterprise readiness", "Secure development", "IT governance", "Audit preparation"],
  },
  {
    id: "telecoms",
    icon: "telecoms",
    name: "Telecommunications",
    description:
      "Telecommunications operators carry critical infrastructure responsibilities and complex supplier ecosystems. We support continuity planning, security assessment and management-system alignment for always-on services.",
    concerns: ["Service continuity", "Critical infrastructure protection", "Supplier risk", "Incident readiness", "Compliance reporting"],
  },
  {
    id: "public-sector",
    icon: "public",
    name: "Public-Sector & Regulated Organizations",
    description:
      "Public-sector and regulated organizations must demonstrate accountability, protect citizen and stakeholder data, and maintain services through disruption. We provide structured, standards-based support.",
    concerns: ["Accountability", "Data protection", "Service continuity", "Procurement standards", "Transparent governance"],
  },
  {
    id: "professional",
    icon: "professional",
    name: "Professional Services",
    description:
      "Firms that handle client confidential information — legal, accounting, consulting and advisory practices — need proportionate security and quality management that protects reputation and client trust.",
    concerns: ["Client confidentiality", "Information handling", "Quality management", "Reputation protection", "Client audits"],
  },
  {
    id: "growing",
    icon: "growth",
    name: "Growing Enterprises",
    description:
      "Scaling organizations often reach the point where informal practices no longer satisfy clients, partners or regulators. We help establish right-sized management systems that grow with the business.",
    concerns: ["Formalizing controls", "First certifications", "Investor due diligence", "Process discipline", "Proportionate governance"],
  },
];

export const APPROACH_STAGES = [
  {
    id: "discover",
    step: "01",
    name: "Discover",
    description:
      "Understand the organization, business objectives, systems, regulatory environment and current challenges.",
  },
  {
    id: "assess",
    step: "02",
    name: "Assess",
    description:
      "Evaluate existing controls, processes, documentation, risks, vulnerabilities and management-system maturity.",
  },
  {
    id: "design",
    step: "03",
    name: "Design",
    description:
      "Develop practical policies, frameworks, controls, roadmaps and remediation plans.",
  },
  {
    id: "implement",
    step: "04",
    name: "Implement",
    description:
      "Support documentation, control implementation, awareness, testing and organizational adoption.",
  },
  {
    id: "improve",
    step: "05",
    name: "Improve",
    description:
      "Monitor performance, address gaps and help the organization sustain continual improvement.",
  },
];

export const ENGAGEMENT_STEPS = [
  { name: "Initial consultation", detail: "A focused conversation about your objectives, context and the challenges you want to address." },
  { name: "Scope confirmation", detail: "Agreement on systems, locations, standards and outcomes covered by the engagement." },
  { name: "Current-state review", detail: "Documentation review, interviews and observation of how work is actually performed." },
  { name: "Assessment and analysis", detail: "Structured evaluation of controls, risks, gaps and management-system maturity." },
  { name: "Recommendations and roadmap", detail: "Prioritized, practical recommendations sequenced into a realistic roadmap." },
  { name: "Implementation support", detail: "Hands-on assistance with documentation, controls, awareness and adoption." },
  { name: "Readiness review", detail: "Verification that arrangements are working and ready for internal or external scrutiny." },
  { name: "Continual-improvement support", detail: "Ongoing monitoring, measurement and refinement to sustain results." },
];

export const APPROACH_PRINCIPLES = [
  { name: "Risk-based & business-aligned", detail: "Recommendations are prioritized by risk and shaped around your business objectives — not generic checklists." },
  { name: "Stakeholder collaboration", detail: "We work alongside leadership, technical teams and process owners so solutions are understood and owned internally." },
  { name: "Clear documentation & knowledge transfer", detail: "Every engagement produces usable documentation and builds internal capability, not dependence." },
  { name: "Practical remediation planning", detail: "Findings become sequenced, achievable actions with clear ownership — not reports that sit on a shelf." },
  { name: "Sustainable continual improvement", detail: "We design management systems to keep improving after the engagement ends." },
  { name: "Confidentiality & professional conduct", detail: "Client information is handled with strict confidentiality and independence throughout." },
];

export const VALUES = [
  { name: "Integrity", detail: "We give honest assessments and independent advice, even when the message is difficult." },
  { name: "Professional excellence", detail: "We hold our work to the standards we help clients implement." },
  { name: "Practicality", detail: "Recommendations must work in your environment, with your people and constraints." },
  { name: "Confidentiality", detail: "Client information, findings and systems are protected without compromise." },
  { name: "Collaboration", detail: "We build capability with your team, not dependency on ours." },
  { name: "Continual improvement", detail: "We practise the improvement discipline we help organizations adopt." },
];

export const CAPABILITIES = [
  "ISO 27001", "ISO 22301", "ISO 9001", "ISO 38500", "PCI DSS",
  "SWIFT CSP", "VAPT", "Cybersecurity", "Risk & Compliance", "Professional Training",
];

export const FAQS = [
  {
    q: "What types of organizations does Numeric Consulting & Solutions support?",
    a: "We support banks and financial-services organizations, fintechs and payment providers, technology and telecommunications companies, public-sector and regulated organizations, professional-services firms and growing enterprises. Our recommendations are shaped around each organization's objectives, environment, risk profile and current maturity.",
  },
  {
    q: "What is an ISO management system?",
    a: "An ISO management system is a structured framework of policies, processes and controls that helps an organization consistently achieve objectives in a particular discipline — such as information security (ISO 27001), business continuity (ISO 22301) or quality (ISO 9001). It provides defined responsibilities, measured performance and a cycle of continual improvement.",
  },
  {
    q: "Can you help an organization prepare for ISO certification?",
    a: "Yes. We provide certification-readiness support: gap assessment, risk assessment, documentation, control implementation, awareness and internal-audit preparation. Certification itself is awarded by independent, accredited certification bodies — our role is to help you build a management system that is genuinely ready for that assessment.",
  },
  {
    q: "What does an ISO 27001 consulting engagement involve?",
    a: "A typical engagement covers scoping, a gap assessment of your current posture, an information-security risk assessment, Statement of Applicability development, policy and control documentation, implementation support, awareness activities and internal-audit readiness. The exact scope depends on your objectives, environment and existing maturity.",
  },
  {
    q: "What is the difference between a vulnerability assessment and a penetration test?",
    a: "A vulnerability assessment systematically identifies and categorizes known weaknesses across an agreed scope. A penetration test goes further: a tester attempts controlled exploitation of weaknesses to validate what an attacker could actually achieve. Assessments give breadth; penetration tests give validated depth. Many organizations benefit from both.",
  },
  {
    q: "Can you support remediation after an assessment?",
    a: "Yes. We help teams interpret findings, prioritize corrective actions by risk, track remediation to completion, validate fixes and strengthen the underlying controls so findings do not recur.",
  },
  {
    q: "Do you provide training for employees and management?",
    a: "Yes. We deliver executive briefings, implementation workshops, employee awareness programmes, internal-auditor training and customized organizational training on ISO management-system standards and related security topics. Content is tailored to roles and to your environment.",
  },
  {
    q: "How do we request an initial consultation?",
    a: "Use the consultation form on our Contact page or reach us through the contact details listed there. Tell us about your objectives and the service area you are interested in, and our team will review your enquiry and respond to arrange an initial discussion.",
  },
];

export const INSIGHT_CATEGORIES = [
  "Information Security",
  "ISO Standards",
  "Business Continuity",
  "Cybersecurity",
  "IT Governance",
  "Risk & Compliance",
  "Training & Awareness",
];

export const WHY_NUMERIC = [
  {
    title: "40+ years of collective experience",
    detail: "A team whose combined background spans consulting, compliance and risk advisory across demanding environments.",
  },
  {
    title: "Cross-functional expertise",
    detail: "Management systems, cybersecurity, continuity and governance expertise working as one integrated practice.",
  },
  {
    title: "Practical, business-aligned recommendations",
    detail: "Advice shaped around your objectives and constraints — designed to be implemented, not shelved.",
  },
  {
    title: "Financial-services & technology depth",
    detail: "Experience supporting environments where regulation, security and uptime are non-negotiable.",
  },
  {
    title: "End-to-end support",
    detail: "From initial assessment through remediation, readiness and continual improvement.",
  },
];
